import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Trash2, MailPlus, Search, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "../context/ShopContext";
import { formatPrice } from "../mock";

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const EMPTY_FORM = { email: "", name: "", phone: "", lang: "it" };

const CustomersAdmin = ({ authHeaders }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [query, setQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/customers`, authHeaders());
      setCustomers(data);
    } catch {
      toast.error("Errore caricamento clienti");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const filtered = customers.filter((u) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      (u.email || "").toLowerCase().includes(q) ||
      (u.name || "").toLowerCase().includes(q) ||
      (u.phone || "").toLowerCase().includes(q)
    );
  });

  const resendInvite = async (user) => {
    setBusyId(user.id);
    try {
      await axios.post(`${API}/admin/customers/${user.id}/resend-invite`, {}, authHeaders());
      toast.success(`Email di invito reinviata a ${user.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (user) => {
    if (!window.confirm(`Eliminare l'account di ${user.email}? Gli ordini non verranno cancellati.`)) return;
    setBusyId(user.id);
    try {
      await axios.delete(`${API}/admin/customers/${user.id}`, authHeaders());
      toast.success("Account eliminato");
      setCustomers((prev) => prev.filter((c) => c.id !== user.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusyId(null);
    }
  };

  const createCustomer = async () => {
    if (!form.email.trim()) { toast.error("L'email è obbligatoria"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/customers`, form, authHeaders());
      toast.success(`Cliente ${data.email} creato`);
      setShowForm(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="idm-admin-section" data-testid="admin-customers">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 6 }}>
        <h3 className="idm-admin-section-title" style={{ margin: 0 }}>
          Clienti ({filtered.length}{query ? ` di ${customers.length}` : ""})
        </h3>
        <button
          className="idm-btn-yellow"
          style={{ animation: "none", display: "flex", alignItems: "center", gap: 6 }}
          onClick={() => { setShowForm((v) => !v); setForm(EMPTY_FORM); }}
        >
          {showForm ? <X size={14} /> : <UserPlus size={14} />}
          {showForm ? "Annulla" : "Nuovo cliente"}
        </button>
      </div>

      <p className="idm-admin-section-sub">
        Account dell'Area Clienti. "Invitato" = creato automaticamente all'acquisto, password non ancora
        impostata (puoi reinviare l'email di invito). L'eliminazione di un account non cancella i suoi ordini.
      </p>

      {/* New customer form */}
      {showForm && (
        <div className="idm-form-grid" style={{ marginBottom: 24, padding: "16px", background: "var(--idm-gray-light, #f5f5f5)", borderRadius: 8 }}>
          <div className="idm-field half">
            <label>Email *</label>
            <input
              type="email"
              placeholder="cliente@esempio.it"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
          <div className="idm-field half">
            <label>Nome e cognome</label>
            <input
              placeholder="Mario Rossi"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div className="idm-field half">
            <label>Telefono</label>
            <input
              placeholder="+39 333 1234567"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </div>
          <div className="idm-field half">
            <label>Lingua</label>
            <select value={form.lang} onChange={(e) => setForm({ ...form, lang: e.target.value })}>
              <option value="it">Italiano</option>
              <option value="en">English</option>
            </select>
          </div>
          <div>
            <button className="idm-btn-yellow" style={{ animation: "none" }} onClick={createCustomer} disabled={saving}>
              {saving ? <Loader2 size={14} className="idm-spin" /> : <UserPlus size={14} />} Crea cliente
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div style={{ position: "relative", marginBottom: 16 }}>
        <Search size={15} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#999" }} />
        <input
          placeholder="Cerca per email, nome o telefono…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ paddingLeft: 32, width: "100%", boxSizing: "border-box" }}
        />
      </div>

      {loading ? (
        <div className="idm-loading"><span className="idm-spinner" /></div>
      ) : filtered.length === 0 ? (
        <p style={{ color: "#999", textAlign: "center", padding: "24px 0" }}>Nessun cliente trovato.</p>
      ) : (
        filtered.map((u) => (
          <div className="idm-admin-prodfile" key={u.id} data-testid={`customer-${u.email}`}>
            <div className="idm-admin-prodfile-info">
              <strong>{u.email}</strong>
              <p>
                {u.name || "—"} | registrato il {fmtDate(u.created_at)} | lingua {(u.lang || "it").toUpperCase()}
                {" | "}ordini pagati: {u.orders_count} ({formatPrice(u.total_spent)})
              </p>
            </div>
            <div className="idm-admin-prodfile-actions">
              <span className={`idm-badge idm-badge-${u.status === "active" ? "paid" : "pending"}`} data-testid={`customer-status-${u.email}`}>
                {u.status === "active" ? "Attivo" : "Invitato"}
              </span>
              {u.status !== "active" && (
                <button
                  className="idm-btn-dark idm-admin-mini"
                  onClick={() => resendInvite(u)}
                  disabled={busyId === u.id}
                  title="Reinvia email di invito"
                  data-testid={`customer-resend-${u.email}`}
                >
                  {busyId === u.id ? <Loader2 size={13} className="idm-spin" /> : <MailPlus size={13} />} Reinvia invito
                </button>
              )}
              <button
                className="idm-admin-prodfile-del"
                onClick={() => remove(u)}
                disabled={busyId === u.id}
                title="Elimina account"
                data-testid={`customer-delete-${u.email}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))
      )}
    </section>
  );
};

export default CustomersAdmin;
